import { FunctionComponent, ReactNode } from "react";
import TransitionWrapper from "../../../components/transition-wrapper/TransitionWrapper";
import { ConfirmTransferScreenProps } from "../useConfirmTransfer";
import ScrollableContainer from "../../../layout/scrollable-container";
import Container from "../../../layout/container";
import Footer from "../../../components/footer";
import styled from "styled-components";
import { COLOR_DEEP_FOREST, COLOR_TEXT_GRAY } from "../../../styles/globalStylesVariables";
import DestinationAccount, { AccountType } from "../../../class/destinationAccount";
import { UNEXPECTED_ERROR_MESSAGE } from "../../../typings";
import { BodyLargeBold, BodyMediumBold, BodyMediumRegular, TitleLarge } from "../../../components/font";
import { Button, tokens } from "@manifesthhqinc/ui-library";
import HeadingContainer from "../../../layout/heading-container";
import cardIcon from "../../../assets/images/card-icon.svg";
import { TransferTotals } from "../../../utils/calcaulateTransferAmount";
import formatCurrency from "../../../utils/formatCurrency";
import Spacing from "../../../layout/spacing";

const SubtotalItem = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin: ${tokens.spacing.xxs} 0;
`;

const StyledTransferDetailsContainer = styled.div<{ $position?: string; $top?: string; $paddingTop?: string }>`
  margin-top: 24px;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  border-radius: 6px;
  background: ${tokens.color.neutral[0]};
  padding: 21px 23px 23px 23px;
  overflow: hidden;

  ${(props) => props.$position && `position: ${props.$position};`}
  ${(props) => props.$top && `top: ${props.$top};`}
  ${(props) => props.$paddingTop && `padding-top: ${props.$paddingTop};`}

  .transfer-from {
    overflow-y: auto;
    margin-bottom: 24px;
  }

  ul {
    display: flex;
    flex-direction: column;
    font-size: 14px;
    list-style-type: none;
    gap: 8px;
  }

  li {
    color: ${COLOR_TEXT_GRAY};
  }

  .title {
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    color: ${COLOR_DEEP_FOREST};
    margin-bottom: 10px;
  }

  .divider{
  width: 100%;
  height: 1px;
  background-color: ${tokens.color.border.card.default};
  margin: 24px 0;
  }}

`;

const transformDestinationDetails = (data: DestinationAccount): string | ReactNode => {
  if (data.destinationAccountType === AccountType.employerSponsored) {
    return data.planName && data.providerName
      ? `${data.planName} - ${data.providerName}`
      : `${data.planSponsor} ${data.providerName ? `- ${data.providerName}` : ""}`;
  }
  if (data.destinationAccountType === AccountType.ANNUITY) {
    return (
      <>
        {data.providerName} Annuity Account
        <br />
        {data.planNumber.slice(-4)}
      </>
    );
  }
  return `IRA - ${data.providerName}`;
};
const ConfirmTransferDetails: FunctionComponent<ConfirmTransferScreenProps> = (props: ConfirmTransferScreenProps) => {
  const { transferDetails, onClickContinue, setShowError, flow, totalTransferAmount, transferType } = props;

  const { preTax = 0, afterTax = 0, roth = 0 } = totalTransferAmount as TransferTotals;

  if (!transferDetails.destinationAccount || transferDetails.transferId === -1) {
    setShowError(UNEXPECTED_ERROR_MESSAGE);
    throw Error(UNEXPECTED_ERROR_MESSAGE);
  }

  return (
    <TransitionWrapper>
      <ScrollableContainer name="confirm-transfer-screen-initiateTransfer">
        <Container>
          <HeadingContainer paneVariant="form" withProgressBar>
            <TitleLarge $textAlign="left">
              {flow === "Annuity" ? "Confirm your transfer details" : "Confirm your details"}
            </TitleLarge>
          </HeadingContainer>
          <StyledTransferDetailsContainer>
            <div className="transfer-from">
              <p className="title">Transfer from:</p>
              <ul>
                {transferDetails.sourceAccounts.map((sourceAccount) => (
                  <li key={sourceAccount.planSponsor}>{sourceAccount.planSponsor}</li>
                ))}
              </ul>
            </div>

            <div className="transfer-to">
              <p className="title">Transfer to:</p>
              <p>{transformDestinationDetails(transferDetails.destinationAccount)}</p>
            </div>
            {flow === "Annuity" && transferType && (
              // Transfer Amount
              // Add Divider

              <>
                <div className="divider" />
                <p className="title">Transfer amount:</p>

                <Spacing token={tokens.spacing.xs} />
                {preTax > 0 && (
                  <SubtotalItem>
                    <BodyMediumRegular $color={tokens.color.text.secondary}>Pre-tax</BodyMediumRegular>{" "}
                    <BodyMediumBold $color={tokens.color.text.highlight}>
                      {transferType === "AMOUNT" ? formatCurrency(preTax) : `${preTax}%`}
                    </BodyMediumBold>
                  </SubtotalItem>
                )}
                {roth > 0 && (
                  <SubtotalItem>
                    <BodyMediumRegular $color={tokens.color.text.secondary}>Roth</BodyMediumRegular>{" "}
                    <BodyMediumBold $color={tokens.color.text.highlight}>
                      {transferType === "AMOUNT" ? formatCurrency(roth) : `${roth}%`}
                    </BodyMediumBold>
                  </SubtotalItem>
                )}
                {afterTax > 0 && (
                  <SubtotalItem>
                    <BodyMediumRegular $color={tokens.color.text.secondary}>After-tax</BodyMediumRegular>{" "}
                    <BodyMediumBold $color={tokens.color.text.highlight}>
                      {transferType === "AMOUNT" ? formatCurrency(afterTax) : `${afterTax}%`}
                    </BodyMediumBold>
                  </SubtotalItem>
                )}
              </>
            )}
          </StyledTransferDetailsContainer>
        </Container>
      </ScrollableContainer>
      <Footer>
        <p className="disclaimer-text no-max-width text-center">
          Next, review your personal details to verify your identity for your transfer.
        </p>
        <Button name="continue" onClick={onClickContinue} size="large">
          Continue
        </Button>
      </Footer>
    </TransitionWrapper>
  );
};

export default ConfirmTransferDetails;
